import React from "react"
import { Link, usePage } from "@inertiajs/react"
import { MapPin, ShoppingBag } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"
import { DinerRealtimeProvider, VisitClosedWatcher } from "@/features/user/diner-realtime"
import { EntryRequestAlert } from "@/features/user/entry-request-alert"
import { PickupCartProvider, usePickupCart } from "@/features/user/pickup-cart"
import {
  CONTEXT_NAV_TABS,
  NO_CONTEXT_NAV_TABS,
  TabBar,
  isTabActive,
  pickupNavTabs,
  waiterCallSlot,
} from "@/features/user/tab-bar"
import { WaiterCallButton } from "@/features/user/waiter-call-button"
import type { DinerSummary, PickupContext } from "@/features/user/types"
import { DINER_HOME_PATH } from "@/lib/diner-home"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/ui/brand-logo"
import { t } from "@/lib/i18n"

type SharedUser = {
  name: string
  email: string
  initials: string
  avatar: string | null
}

/**
 * Which chrome the screen wears, declared by the route's controller. `takeover`
 * is a focused flow — an invite, a dish detail, the farewell — that drops the
 * navigation entirely.
 *
 * This used to be a `hideTabBar` prop on 8 individual pages, which meant the
 * server could not tell you whether a screen had a tab bar; you had to open its
 * component. See `User::BaseController.shell_mode`.
 */
type ShellMode = "visit" | "account" | "join" | "takeover" | "pickup"

type UserLayoutProps = {
  children: React.ReactNode
  cartCount?: number
  /** Browsing screens (menu) widen to a multi-column layout on desktop. */
  wide?: boolean
  /**
   * Gives the page exactly one viewport of height so it can pin a footer of its
   * own (the bill's pay panel) and scroll its list inside. The Menny footer then
   * starts below the fold instead of competing for the bottom edge.
   */
  fillsViewport?: boolean
  /**
   * Overrides the shared diner context in the header. Pre-visit screens
   * (invite, presence) and past bills name a location the diner is not
   * currently seated at.
   */
  locationName?: string
}

/**
 * Mounts the pickup cart before the shell reads it. The provider has to sit
 * above `UserShell` rather than inside it, because the tab bar's badge and the
 * cart screen are both downstream of the same cart — one lives in the chrome and
 * the other in the page.
 */
export function UserLayout(props: UserLayoutProps) {
  const pickup = usePage<{ pickup?: PickupContext | null }>().props.pickup

  if (pickup?.slug) {
    return (
      <PickupCartProvider slug={pickup.slug}>
        <UserShell {...props} />
      </PickupCartProvider>
    )
  }

  return <UserShell {...props} />
}

function UserShell({
  children,
  cartCount: cartCountProp,
  wide = false,
  fillsViewport = false,
  locationName,
}: UserLayoutProps) {
  const { props } = usePage<{
    user: SharedUser | null
    cartCount?: number
    diner?: DinerSummary
    pickup?: PickupContext | null
    shellMode?: ShellMode
  }>()
  const { user, diner, pickup } = props
  const currentPath = usePage().url
  const pickupCart = usePickupCart()

  // `diner` is null exactly when there is no active visit — see
  // User::DinerSerialization#serialize_diner. `pickup` is the other way to be
  // inside a restaurant: addressed by slug, with no visit behind it. Together
  // they are the whole mode switch — outside both, the menu and the bill do not
  // exist and the server bounces them, so the shell must stop offering them
  // rather than let the diner tap into an error.
  //
  // **A visit is a dine-in context only when it has a table.** `serviceContext`
  // is computed as exactly `at_table?`, so a table-less visit is a pickup one,
  // and the destinations below are the table's: `User::VisitController` requires
  // a table and bounces anyone else. Reading this as a plain `Boolean(diner)`
  // is what put `/dinein/menu` and `/dinein/cart` in the tab bar of a diner with
  // no table — the `pickup` prop only exists on the slug-addressed screens, so
  // there was nothing left to tell the two apart out here.
  const dineIn = Boolean(diner) && diner?.serviceContext !== "pickup"
  const hasContext = dineIn || Boolean(pickup)

  // The count is server-side for a visit, where the bill is the truth, and
  // client-side for pickup, where the cart has never left the browser.
  const cartCount = cartCountProp ?? (pickup ? (pickupCart?.count ?? 0) : (props.cartCount ?? 0))

  const overridesLocation = locationName !== undefined
  const headerLocation = overridesLocation
    ? locationName
    : (diner?.locationName ?? pickup?.locationName ?? null)
  const isPickup = diner?.serviceContext === "pickup" || Boolean(pickup)

  // Both of these used to be `/menu` and `/cart`, which are not diner routes at
  // all: `/menu` exists only on staff.menny.app and `/cart` exists nowhere. They
  // are what the `/cart` → `/dinein/cart` rename left behind, and they 404'd from
  // the desktop header's cart icon and from the restaurant's name in both
  // headers. The mobile tab bar was never affected — it builds its hrefs from
  // `USER_NAV_TABS`, which moved with the routes.
  const menuHref = pickup ? `/r/${pickup.slug}` : "/dinein/menu"
  const cartHref = pickup ? `/r/${pickup.slug}/cart` : "/dinein/cart"

  // Route as well as context: the call lives on the /dinein screens and nowhere
  // else. `waiterCallSlot` owns the whole rule and says why.
  const waiterCall = waiterCallSlot({ shellMode: props.shellMode, diner, isPickup })

  // A takeover is a focused flow — an invite, a dish detail, the farewell. It
  // gets no navigation at all: offering a diner mid-flow a way out of it is the
  // same mistake in either corner, so the no-context destinations go too.
  const takeover = props.shellMode === "takeover" || props.shellMode === "join"

  const showTabBar = hasContext && !takeover
  const showNoContextNav = !hasContext && !takeover

  const columnWidth = wide ? "max-w-6xl" : "max-w-2xl"

  return (
    <DinerRealtimeProvider>
      <VisitClosedWatcher />
      <div
        className="flex h-dvh flex-col bg-background pt-[env(safe-area-inset-top)]"
        data-testid="mobile-shell"
        data-shell-mode={hasContext ? "in-context" : "no-context"}
      >
        {/* Desktop-only top-nav. */}
        <header className="hidden h-12 w-full shrink-0 items-center gap-4 px-4 md:mt-3 md:flex">
          {headerLocation ? (
            <LocationMark
              name={headerLocation}
              isPickup={isPickup}
              href={hasContext ? menuHref : null}
              className="flex min-w-0 flex-col leading-tight"
              nameClassName="max-w-[18rem] font-bold"
              testId="header-location-name"
            />
          ) : (
            <BrandMark href={DINER_HOME_PATH} />
          )}

          <nav
            className="ml-auto flex shrink-0 items-center gap-1"
            aria-label={t("ui.navigation.navigation_menu")}
          >
            {(hasContext || showNoContextNav) && (
              <NavIconLinks
                tabs={
                  pickup
                    ? pickupNavTabs(pickup.slug).slice(0, 1)
                    : hasContext
                      ? CONTEXT_NAV_TABS
                      : NO_CONTEXT_NAV_TABS
                }
                currentPath={currentPath}
                testIdPrefix="desktop-tab"
              />
            )}

            {hasContext && (
              <Link
                href={cartHref}
                data-testid="desktop-tab-cart"
                className="relative inline-flex"
                aria-label={t("ui.react.features.user.tab_bar.bill")}
              >
                <Button variant="ghost" size="icon">
                  <ShoppingBag className="size-[18px]" />
                </Button>
                {cartCount > 0 && <CountBadge count={cartCount} className="-right-0.5 -top-0.5" />}
              </Link>
            )}

            {/* Desktop has no tab bar, so the call has to live here or the
                capability would be mobile-only. */}
            {waiterCall && (
              <WaiterCallButton
                testId="desktop-waiter-call-button"
                pending={waiterCall.pending}
                className="ml-1"
              />
            )}

            {user && <ProfileAvatarLink user={user} testId="desktop-profile-link" />}
          </nav>
        </header>

        {/* Mobile counterpart. Inside a restaurant it names where you are and the
            tab bar carries navigation; outside one there is no tab bar, so the
            two destinations that still work live up here next to the avatar. */}
        <DinerTopBar
          className="md:hidden"
          user={user}
          locationName={headerLocation}
          isPickup={isPickup}
          showNav={showNoContextNav}
          locationHref={hasContext ? menuHref : null}
          currentPath={currentPath}
        />

        {/* Above the scroll area on purpose: a person is waiting in front of a
            QR, and the answer should not depend on where the diner happens to
            have scrolled. */}
        <EntryRequestAlert />

        <div className="flex min-h-0 w-full flex-1 flex-col">
          {/* The scroll area is the flex column itself, so the page box below has
              a definite height to measure against. `flex-1` keeps the footer
              pinned to the bottom on short pages; `h-full` fixes the page at
              exactly one viewport — a ceiling, not a floor, so a long list
              scrolls inside the page instead of pushing its own footer off
              screen — and drops the Menny footer below the fold.

              It spans the viewport rather than the reading column so the
              scrollbar rides the window edge instead of drawing a second, inner
              border alongside the content. The column width moves onto the two
              children, which stay direct flex children of the scroller — that is
              what keeps `h-full` and `flex-1` resolving against a definite
              height. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div
              className={cn(
                "mx-auto w-full",
                columnWidth,
                fillsViewport ? "flex h-full shrink-0 flex-col" : "flex-1"
              )}
            >
              {children}
            </div>
            <UserFooter className={cn("mx-auto w-full", columnWidth)} />
          </div>
          {showTabBar && (
            <TabBar
              cartCount={cartCount}
              waiterCall={waiterCall}
              tabs={
                pickup
                  ? { before: pickupNavTabs(pickup.slug).slice(0, 1), after: pickupNavTabs(pickup.slug).slice(1) }
                  : undefined
              }
              className="md:hidden"
            />
          )}
        </div>
        {/* Offset past the shell's own top bar. Without it a toast lands at the
            viewport edge, where the header is painted over it and the first line
            is clipped off-screen — which is how a two-line message became
            unreadable on a phone while `toBeVisible()` still passed, since
            Playwright's visibility check does not require being in the viewport.
            The inset is part of the sum because the shell pads for the notch, so
            a fixed offset that clears the header on a simulator slides back under
            it on a real device. */}
        <Toaster
          position="top-right"
          richColors
          offset={{ top: "calc(env(safe-area-inset-top) + 4rem)", right: "1rem" }}
          mobileOffset={{
            top: "calc(env(safe-area-inset-top) + 4.25rem)",
            left: "1rem",
            right: "1rem",
          }}
        />
      </div>
    </DinerRealtimeProvider>
  )
}

type DinerTopBarProps = {
  user: SharedUser | null
  locationName: string | null
  isPickup: boolean
  /**
   * The no-context destinations only appear where the tab bar would have, had
   * there been a visit. A focused flow that hides the bar — an invite, the
   * farewell — hides these too, or the screen would be offering a diner
   * mid-flow a way to abandon it.
   */
  showNav: boolean
  /** Where the restaurant's name goes. Null when there is no menu behind it. */
  locationHref: string | null
  currentPath: string
  className?: string
}

function DinerTopBar({
  user,
  locationName,
  isPickup,
  showNav,
  locationHref,
  currentPath,
  className,
}: DinerTopBarProps) {
  return (
    <header
      data-testid="diner-top-bar"
      className={cn(
        "flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5",
        className
      )}
    >
      {locationName ? (
        <LocationMark
          name={locationName}
          isPickup={isPickup}
          href={locationHref}
          className="min-w-0 flex-1"
          testId="diner-top-bar-location"
          withPin
        />
      ) : (
        <BrandMark href={DINER_HOME_PATH} className="min-w-0 flex-1" />
      )}

      {showNav && (
        <nav
          className="flex shrink-0 items-center gap-1"
          aria-label={t("ui.navigation.navigation_menu")}
        >
          <NavIconLinks
            tabs={NO_CONTEXT_NAV_TABS}
            currentPath={currentPath}
            testIdPrefix="top-bar-tab"
          />
        </nav>
      )}

      {user && <ProfileAvatarLink user={user} testId="mobile-profile-link" />}
    </header>
  )
}

function NavIconLinks({
  tabs,
  currentPath,
  testIdPrefix,
}: {
  tabs: { id: string; label: string; href: string; icon: React.ElementType }[]
  currentPath: string
  testIdPrefix: string
}) {
  return (
    <>
      {tabs.map((tab) => {
        const Icon = tab.icon
        const active = isTabActive(currentPath, tab.href)
        return (
          <Link key={tab.id} href={tab.href} data-testid={`${testIdPrefix}-${tab.id}`}>
            <Button
              variant={active ? "default" : "ghost"}
              size="icon"
              aria-label={tab.label}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="size-[18px]" />
            </Button>
          </Link>
        )
      })}
    </>
  )
}

/**
 * Names where the diner is. Only a link when a visit is running: on a past
 * receipt or a pre-visit invite there is no menu behind that name, and tapping
 * it would land on the redirect-plus-error the shell exists to avoid.
 *
 * The eyebrow never claims a table. This renders on overridden screens too,
 * where the table is unknown rather than absent, so it stays at "Estás en".
 */
function LocationMark({
  name,
  isPickup,
  href,
  className,
  nameClassName,
  testId,
  withPin = false,
}: {
  name: string
  isPickup: boolean
  href: string | null
  className?: string
  nameClassName?: string
  testId?: string
  withPin?: boolean
}) {
  const body = (
    <>
      <span className="block text-xs font-medium text-muted-foreground">
        {isPickup
          ? t("ui.react.features.user.home_screen.pickup_context")
          : t("ui.react.features.user.home_screen.text")}
      </span>
      <span
        className={cn(
          "flex items-center gap-1.5 truncate text-base font-semibold tracking-tight",
          nameClassName
        )}
      >
        {withPin && <MapPin className="size-4 shrink-0 text-muted-foreground" />}
        {name}
      </span>
    </>
  )

  if (!href) {
    return (
      <div className={className} data-testid={testId}>
        {body}
      </div>
    )
  }

  return (
    <Link href={href} className={className} data-testid={testId}>
      {body}
    </Link>
  )
}

function BrandMark({ href, className }: { href: string; className?: string }) {
  return (
    <Link href={href} className={cn("flex items-center gap-2", className)}>
      <BrandLockup />
    </Link>
  )
}

function CountBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span
      className={cn(
        "absolute flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-brand-solid px-1 text-xs font-bold tabular-nums text-brand-foreground",
        className
      )}
    >
      {count}
    </span>
  )
}

function ProfileAvatarLink({ user, testId }: { user: SharedUser; testId: string }) {
  return (
    <Link
      href="/profile"
      data-testid={testId}
      className="ml-0.5 inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Avatar className="size-8 shrink-0">
        <AvatarImage src={user.avatar || ""} alt={user.name} />
        <AvatarFallback className="text-xs">{user.initials}</AvatarFallback>
      </Avatar>
      <span className="sr-only">{t("ui.navigation.profile")}</span>
    </Link>
  )
}

function UserFooter({ className }: { className?: string }) {
  return (
    <footer
      className={cn("mt-10 border-t px-4 py-6 text-center", className)}
      data-testid="user-footer"
    >
      {/* The full lockup, not the wordmark alone. BRAND.md §20 lists footers under
          "wordmark alone", and this is a deliberate departure: on the public pickup
          menu this footer is the ONLY place Menny appears, so the symbol has to be
          in it or the frame §10 describes has no mark at all. It is `h-6` because
          the lockup's minimum is 88px wide and the old `h-3.5` would have put it at
          57px — below the size at which it was drawn to hold together.

          `mx-auto` and not the footer's own `text-center`: Tailwind's preflight
          makes every svg `display: block`, so centring text never reaches it. The
          wordmark this replaced carried an explicit `inline-block` for exactly
          that reason. */}
      <BrandLockup className="mx-auto" />
    </footer>
  )
}
